"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DeleteScoreResponse = exports.DeleteScoreQuery = exports.PostScoresResponse = exports.PostScoresBody = exports.PostScoreBody = exports.GetScoresQuery = exports.GetScoreQuery = void 0;
const zod_1 = require("zod");
const zod_2 = require("../../../../utils/zod");
const typeChecks_1 = require("../../../../utils/typeChecks");
const scores_1 = require("../../../../utils/scores");
const shared_1 = require("../shared");
const scores_2 = require("../../../../domain/scores");
const filters_1 = require("../../../../interfaces/filters");
const errors_1 = require("../../../../errors");
const operators = ["<", ">", "<=", ">=", "!=", "="];
const SCORE_FIELD_GROUPS = ["score", "trace"];
/**
 * Endpoints
 */
// GET /scores/{scoreId}
exports.GetScoreQuery = zod_1.z.object({
    scoreId: zod_1.z.string(),
});
// GET /scores
exports.GetScoresQuery = zod_1.z.object({
    ...zod_2.publicApiPaginationZod,
    userId: zod_1.z.string().nullish(),
    dataType: scores_2.ScoreDataTypeDomain.nullish(),
    configId: zod_1.z.string().nullish(),
    queueId: zod_1.z.string().nullish(),
    traceTags: zod_1.z.union([zod_1.z.array(zod_1.z.string()), zod_1.z.string()]).nullish(),
    environment: zod_1.z.union([zod_1.z.array(zod_1.z.string()), zod_1.z.string()]).nullish(),
    name: zod_1.z.string().nullish(),
    fromTimestamp: typeChecks_1.stringDateTime,
    toTimestamp: typeChecks_1.stringDateTime,
    source: scores_2.ScoreSourceDomain.nullish(),
    value: zod_1.z.coerce.number().nullish(),
    operator: zod_1.z.enum(operators).nullish(),
    scoreIds: zod_1.z
        .string()
        .transform((str) => str.split(",").map((id) => id.trim())) // Split the comma-separated string
        .refine((arr) => arr.every((id) => typeof id === "string"), {
        message: "Each score ID must be a string",
    })
        .nullish(),
    fields: (0, zod_2.commaSeparatedEnumArray)(SCORE_FIELD_GROUPS, null, {
        unknownValues: "filter",
    }),
    filter: zod_1.z
        .string()
        .optional()
        .transform((str) => {
        if (!str)
            return undefined;
        try {
            const parsed = JSON.parse(str);
            return parsed;
        }
        catch (e) {
            if (e instanceof errors_1.InvalidRequestError)
                throw e;
            throw new errors_1.InvalidRequestError("Invalid JSON in filter parameter");
        }
    })
        .pipe(filters_1.singleFilterList.optional()),
});
// POST /scores
// Roughly mirrors ScoreBody in `packages/shared/src/server/ingestion/types.ts`;
// keep them in sync for fields that cross both surfaces.
exports.PostScoreBody = (0, scores_1.applyScoreValidation)(shared_1.PostScoreBodyFoundationSchema.extend({
    source: scores_2.PublicApiCreateScoreSourceDomain.default(scores_2.ScoreSourceEnum.API),
}).and(zod_1.z.discriminatedUnion("dataType", [
    zod_1.z.object({
        value: zod_1.z.number(),
        dataType: zod_1.z.literal("NUMERIC"),
        configId: zod_1.z.string().nullish(),
    }),
    zod_1.z.object({
        value: zod_1.z.string(),
        dataType: zod_1.z.literal("CATEGORICAL"),
        configId: zod_1.z.string().nullish(),
    }),
    zod_1.z.object({
        value: zod_1.z.number().refine((value) => value === 0 || value === 1, {
            message: "Value must be a number equal to either 0 or 1 for data type BOOLEAN",
        }),
        dataType: zod_1.z.literal("BOOLEAN"),
        configId: zod_1.z.string().nullish(),
    }),
    zod_1.z.object({
        value: zod_1.z.string(), // Corrected output text
        dataType: zod_1.z.literal("CORRECTION"),
        configId: zod_1.z.undefined().nullish(), // Cannot have config
    }),
    zod_1.z.object({
        value: zod_1.z.string().min(1).max(scores_2.TEXT_SCORE_MAX_LENGTH),
        dataType: zod_1.z.literal("TEXT"),
        configId: zod_1.z.string().nullish(),
    }),
    zod_1.z.object({
        value: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
        dataType: zod_1.z.undefined(),
        configId: zod_1.z.string().nullish(),
    }),
]))).refine((data) => !(0, scores_2.isAnnotationScoreMissingConfigId)(data), {
    message: scores_2.ANNOTATION_SCORE_REQUIRES_CONFIG_ID_MESSAGE,
    path: ["configId"],
});
// Batch items are validated individually so invalid scores do not reject valid siblings.
exports.PostScoresBody = zod_1.z.union([exports.PostScoreBody, zod_1.z.array(zod_1.z.unknown())]);
exports.PostScoresResponse = zod_1.z.union([
    zod_1.z.object({ id: zod_1.z.string() }),
    zod_1.z.object({ message: zod_1.z.string() }),
    zod_1.z.object({
        accepted: zod_1.z.number().int().nonnegative(),
        rejected: zod_1.z.number().int().nonnegative(),
        errors: zod_1.z.array(zod_1.z.object({ message: zod_1.z.string() })),
    }),
]);
// DELETE /scores/{scoreId}
// Please note that the DELETE /scores/{scoreId} endpoint supports all score types (trace, session, dataset run) across v1 and v2.
exports.DeleteScoreQuery = zod_1.z.object({
    scoreId: zod_1.z.string(),
});
exports.DeleteScoreResponse = zod_1.z.object({
    message: zod_1.z.string(),
});
